(() => {
  if (customElements.get('ring-size-modal')) return;

  const TRIGGER_SELECTOR = '[data-size-chart-toggle]';

  // Same rule variant-picker.js uses to tell the PDP apart from quick-add / product cards,
  // which keep their own size chart.
  const NON_PDP_SELECTOR = 'quick-add-dialog, product-card';

  // Set on <html> while the drawer is open; drives the scroll lock and header fix in ring-size-modal.css.
  const OPEN_CLASS = 'ring-size-guide-open';

  /**
   * The drawer stays closed until the size chart button is clicked, so its stylesheet is added to <head>
   * here instead of blocking the PDP render. <head> also sits outside the <main> morph that runs on
   * combined-listing variant changes, so the stylesheet is never touched by it.
   * @param {string | undefined} href
   * @returns {Promise<void>} Settles once the stylesheet has loaded or failed to load.
   */
  function loadStylesheet(href) {
    if (!href) return Promise.resolve();

    if (document.querySelector(`link[href="${href}"]`)) {
      return Promise.resolve();
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    const settled = new Promise((resolve) => {
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
    });
    document.head.append(link);
    return settled;
  }

  function getStylesheetHref() {
    return (
      document.currentScript?.dataset?.stylesheet ||
      document.querySelector('ring-size-modal')?.getAttribute('data-stylesheet') ||
      document.querySelector('script[data-stylesheet*="ring-size-modal"]')?.getAttribute('data-stylesheet') ||
      ''
    );
  }

  const stylesheetReady = loadStylesheet(getStylesheetHref());

  /**
   * Ring size guide drawer. Wraps a native <dialog>, which provides the modal
   * semantics, Escape handling, focus containment and background inertness.
   */
  class RingSizeModal extends HTMLElement {
    /** @type {HTMLDialogElement | null} */
    #dialog = null;

    /** @type {HTMLElement | null} */
    #trigger = null;

    /** Whether the current press started on the backdrop (not a text-selection drag out of the panel). */
    #pressStartedOnBackdrop = false;

    connectedCallback() {
      this.#dialog = this.querySelector('dialog');
      this.addEventListener('pointerdown', this.#handlePointerDown);
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleTabKeydown);
      this.#dialog?.addEventListener('close', this.#handleClose);
    }

    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.#handlePointerDown);
      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('keydown', this.#handleTabKeydown);
      this.#dialog?.removeEventListener('close', this.#handleClose);
      // Removed while open (no close event follows): release the scroll lock.
      document.documentElement.classList.remove(OPEN_CLASS);
    }

    /**
     * Opens the drawer in its default state.
     * @param {HTMLElement} trigger - The element that receives focus again on close.
     */
    async open(trigger) {
      if (!this.#dialog || this.#dialog.open) return;

      const href = this.dataset.stylesheet || getStylesheetHref();
      if (href && !document.querySelector(`link[href="${href}"]`)) {
        await loadStylesheet(href);
      } else {
        // Only waits when the button is clicked before the stylesheet has arrived.
        await stylesheetReady;
      }
      if (this.#dialog.open) return;

      this.#trigger = trigger;
      this.#selectTab(this.dataset.defaultTab ?? '');
      this.#selectMethod(this.dataset.defaultMethod ?? '');
      document.documentElement.classList.add(OPEN_CLASS);
      this.#dialog.showModal();

      const body = this.querySelector('[data-ring-size-body]');
      if (body) body.scrollTop = 0;
    }

    close() {
      this.#dialog?.close();
    }

    /** @param {PointerEvent} event */
    #handlePointerDown = (event) => {
      this.#pressStartedOnBackdrop = event.target === this.#dialog;
    };

    /** @param {MouseEvent} event */
    #handleClick = (event) => {
      const { target } = event;
      if (!(target instanceof Element)) return;

      // The panel fills the dialog, so a press on the dialog element itself is a press on the backdrop.
      const isBackdropClick = target === this.#dialog && this.#pressStartedOnBackdrop;
      if (isBackdropClick || target.closest('[data-ring-size-close]')) {
        this.close();
        return;
      }

      const tab = target.closest('[data-ring-size-tab]');
      if (tab instanceof HTMLElement) {
        this.#selectTab(tab.dataset.ringSizeTab ?? '');
        return;
      }

      const methodButton = target.closest('[data-ring-size-method]');
      if (methodButton instanceof HTMLElement) {
        this.#selectMethod(methodButton.dataset.ringSizeMethod ?? '');
      }
    };

    /**
     * Arrow / Home / End navigation between tabs (WAI-ARIA tabs pattern, automatic activation).
     * @param {KeyboardEvent} event
     */
    #handleTabKeydown = (event) => {
      const { target } = event;
      if (!(target instanceof HTMLElement) || !target.matches('[data-ring-size-tab]')) return;

      const tabs = this.#tabs;
      const index = tabs.indexOf(target);
      const lastIndex = tabs.length - 1;
      /** @type {Record<string, number>} */
      const nextIndexByKey = {
        ArrowRight: index === lastIndex ? 0 : index + 1,
        ArrowLeft: index === 0 ? lastIndex : index - 1,
        Home: 0,
        End: lastIndex,
      };
      const nextIndex = nextIndexByKey[event.key];
      const nextTab = nextIndex === undefined ? undefined : tabs[nextIndex];
      if (!nextTab) return;

      event.preventDefault();
      this.#selectTab(nextTab.dataset.ringSizeTab ?? '');
      nextTab.focus();
    };

    #handleClose = () => {
      document.documentElement.classList.remove(OPEN_CLASS);
      if (this.#trigger?.isConnected) this.#trigger.focus();
      this.#trigger = null;
    };

    /** @returns {HTMLElement[]} */
    get #tabs() {
      return Array.from(this.querySelectorAll('[data-ring-size-tab]'));
    }

    /**
     * Shows one tab panel. Falls back to the first tab for an unknown name.
     * @param {string} name
     */
    #selectTab(name) {
      const tabs = this.#tabs;
      const activeName = tabs.some((tab) => tab.dataset.ringSizeTab === name) ? name : tabs[0]?.dataset.ringSizeTab;

      for (const tab of tabs) {
        const isActive = tab.dataset.ringSizeTab === activeName;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;

        const panelId = tab.getAttribute('aria-controls');
        const panel = panelId ? this.querySelector(`#${panelId}`) : null;
        if (panel instanceof HTMLElement) panel.hidden = !isActive;
      }
    }

    /**
     * Shows the step list of one measuring method; the content around it stays unchanged.
     * Falls back to the first method for an unknown name.
     * @param {string} name
     */
    #selectMethod(name) {
      /** @type {HTMLElement[]} */
      const buttons = Array.from(this.querySelectorAll('[data-ring-size-method]'));
      const activeName = buttons.some((button) => button.dataset.ringSizeMethod === name)
        ? name
        : buttons[0]?.dataset.ringSizeMethod;

      for (const button of buttons) {
        button.setAttribute('aria-pressed', String(button.dataset.ringSizeMethod === activeName));
      }

      for (const steps of this.querySelectorAll('[data-ring-size-steps]')) {
        if (steps instanceof HTMLElement) steps.hidden = steps.dataset.ringSizeSteps !== activeName;
      }
    }
  }

  customElements.define('ring-size-modal', RingSizeModal);

  // Delegated at the document level: the trigger lives inside <variant-picker>,
  // which is re-rendered (morphed) on every variant change.
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!(trigger instanceof HTMLElement) || trigger.closest(NON_PDP_SELECTOR)) return;

    // Quick-add loads the product page markup, so a second copy of the drawer can exist inside it.
    const modal = Array.from(document.querySelectorAll('ring-size-modal')).find(
      (element) => !element.closest(NON_PDP_SELECTOR)
    );
    if (!(modal instanceof RingSizeModal)) return;

    event.preventDefault();
    modal.open(trigger);
  });
})();
